import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Sparkles, FileText } from "lucide-react";
import { toast } from "sonner";
import { useOnline } from "@/lib/useOnline";
import { OfflineNotice } from "@/components/OfflineNotice";
import { useAnimal } from "@/lib/repo";

const DIAGNOSIS_OPTS = [
  { v: "pregnant", t: "Preñada" },
  { v: "empty", t: "Vacía" },
  { v: "empty_cull", t: "Vacía descarte" },
];

export default function AIReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const online = useOnline();
  const animal = useAnimal(id);
  const [diagnosis, setDiagnosis] = useState("pregnant");
  const [months, setMonths] = useState("2");
  const [notes, setNotes] = useState("Animal en buen estado corporal.");
  const [report, setReport] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!online) return toast.error("Necesitás internet para generar el reporte IA");
    setLoading(true); setReport("");
    try {
      const base = { diagnosis, months: diagnosis === "pregnant" ? Number(months) || null : null, vet_notes: notes };
      const payload = animal ? {
        animal_id: animal.id, chip_code: animal.chip_code, lomo_number: animal.lomo_number,
        breed: animal.breed, sex: animal.sex, birth_date: animal.birth_date,
        category: animal.category, ...base,
      } : base;
      const r = await api.aiPalpationReport(payload);
      setReport(r.markdown);
      toast.success("Reporte generado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pude generar el reporte");
    } finally { setLoading(false); }
  };

  const today = new Date().toLocaleDateString("es-AR", { day: "2-digit", month: "long", year: "numeric" });

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={()=>navigate(-1)} className="h-9 w-9"><ArrowLeft size={18} /></Button>
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-muted-foreground">Asistencia veterinaria</div>
          <h1 className="font-editorial text-[28px] sm:text-[36px] leading-[1.05] tracking-tight">Reporte de palpación</h1>
        </div>
      </div>

      {/* Input form */}
      <Card>
        <CardHeader>
          <div className="text-[10.5px] font-mono uppercase tracking-[0.12em] text-muted-foreground flex items-center gap-2">
            <Sparkles size={12} className="text-primary"/>Datos para el análisis IA
          </div>
          <CardTitle className="text-[17px] mt-0.5">Preñez · condición corporal · hallazgos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {animal && (
            <div className="rounded-[calc(var(--radius)-0.35rem)] border border-border bg-muted/40 p-3 sm:p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12.5px]">
              <Meta k="Lomo" v={animal.lomo_number || "—"} />
              <Meta k="Raza" v={animal.breed || "—"} />
              <Meta k="Sexo" v={animal.sex === "female" ? "Hembra" : "Macho"} />
              <Meta k="Categoría" v={animal.category || "—"} />
              <Meta k="Chip RFID" v={animal.chip_code} mono full />
            </div>
          )}
          <div>
            <Label className="text-[12px] text-muted-foreground">Diagnóstico</Label>
            <Select value={diagnosis} onValueChange={setDiagnosis}>
              <SelectTrigger className="h-11 mt-1" data-testid="ai-report-diagnosis"><SelectValue /></SelectTrigger>
              <SelectContent>{DIAGNOSIS_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {diagnosis === "pregnant" && (
            <div>
              <Label className="text-[12px] text-muted-foreground">Meses de preñez</Label>
              <Input data-testid="ai-report-months" type="number" inputMode="decimal" value={months}
                onChange={(e) => setMonths(e.target.value)} placeholder="Ej. 2.5" className="h-11 mt-1" />
            </div>
          )}
          <div>
            <Label className="text-[12px] text-muted-foreground">Notas del veterinario</Label>
            <Textarea data-testid="ai-report-notes" value={notes} onChange={(e)=>setNotes(e.target.value)} rows={3} className="mt-1" />
          </div>
          {!online && <OfflineNotice label="El reporte de palpación con IA requiere conexión." />}
          <Button onClick={generate} disabled={loading || !online} size="lg" className="w-full" data-testid="ai-report-generate">
            {loading ? "Generando con IA…" : <><Sparkles size={16}/>Generar reporte clínico</>}
          </Button>
        </CardContent>
      </Card>

      {/* Clinical report output */}
      {report && (
        <Card className="fade-in">
          <CardHeader className="border-b border-border">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-muted-foreground flex items-center gap-2">
                  <FileText size={12}/>Informe veterinario
                </div>
                <CardTitle className="text-[20px] mt-0.5 font-editorial italic">Análisis de palpación</CardTitle>
              </div>
              <div className="text-right">
                <div className="text-[10.5px] font-mono uppercase tracking-[0.12em] text-muted-foreground">Emitido</div>
                <div className="text-[12.5px] font-mono tabular-nums text-foreground">{today}</div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-5">
            <article
              data-testid="ai-report-output"
              className="text-[14.5px] text-foreground leading-relaxed whitespace-pre-wrap font-editorial"
              style={{ fontVariationSettings: "'opsz' 14" }}
            >
              {report}
            </article>
            <div className="mt-6 pt-4 border-t border-border">
              <p className="text-[11.5px] font-mono uppercase tracking-[0.12em] text-muted-foreground mb-1">Aviso profesional</p>
              <p className="text-[12.5px] text-muted-foreground italic leading-relaxed">
                Este informe es una asistencia generada con inteligencia artificial. La decisión clínica final corresponde al médico veterinario tratante.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Meta({ k, v, mono, full }) {
  return (
    <div className={full ? "col-span-full" : ""}>
      <div className="text-[10px] font-mono uppercase tracking-[0.12em] text-muted-foreground">{k}</div>
      <div className={`text-foreground mt-0.5 ${mono ? "font-mono tabular-nums tracking-[0.04em]" : "font-medium"}`}>{v}</div>
    </div>
  );
}
